import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { Box, Typography, CircularProgress, Grid } from "@mui/material";
import NoDataAvailabWithNoCardleScreen from "../../../../../components/screens/NoDataAvailabeWithNoCard";
import Loader from "../../../../../../SAPPages/components/Loader/Loader";

export default function ComparisonProgressChart({
  overallData,
  loading,
  testTypeData,
}) {
  // State to hold the chart data
  const [chartData, setChartData] = useState({
    series: [],
    options: {
      chart: {
        type: "bar",
        height: 245,
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          horizontal: false, // Vertical bars
          dataLabels: {
            position: "top", // Display data labels above bars
          },
          borderRadiusApplication: "end",
          columnWidth: "30px", // Adjust this to change the width of the bars (in percentage)

          barGroupPadding: 0.3, // Add spacing between bars in the group
          states: {
            hover: {
              // Disable hover effect
              filter: "none", // No filter on hover (no color change)
            },
          },
        },
      },
      colors: [], // Colors for each bar (Test 1, Test 2, Test 3)
      dataLabels: {
        enabled: false,
        style: {
          fontSize: "12px",
          fontWeight: "bold",
          colors: ["#fff"],
        },
      },
      stroke: {
        // added space between bar
        show: true,
        width: 2,
        colors: ["transparent"],
      },
      xaxis: {
        categories: [], // Categories will be populated by the API
        title: {
          style: {
            fontSize: "14px",
            fontWeight: "bold",
            color: "#4f4f4f",
          },
        },
      },
      yaxis: {
        title: {
          style: {
            fontSize: "14px",
            fontWeight: "bold",
            color: "#4f4f4f",
          },
        },
        max: 10, // Max value for the Y-axis
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return `Band ${val}/10`; // Formatting tooltip values
          },
        },
      },
      legend: {
        show: false, // Hide legend
      },
    },
  });

  // Loading state

  // Fetch data from API on component mount

  useEffect(() => {
    // Check if overallData is available and has the necessary properties
    if (!hasOverallData(overallData?.overallData)) {
      setChartData((prev) => ({ ...prev, series: [] }));
      return;
    }

    // 1️⃣ Keep only categories that have data
    const filteredEntries = Object?.entries(
      overallData?.overallData || {}
    ).filter(([, value]) => value?.length > 0);

    // 2️⃣ Categories (x-axis)
    const categories = filteredEntries.map(([key]) => key); //  ["Reading", "Writing", "Listening", "Speaking", "Mock"];

    const maxLength = Math.max(
      ...Object.values(overallData?.overallData || {}).map((arr) => arr.length)
    );

    const series = Array.from({ length: maxLength }, (_, i) => ({
      name: `Test ${i + 1}`,
      data: Object.values(overallData?.overallData)
        .map((arr) => arr[i]?.band)
        .filter((band) => band !== undefined)
        .map(Number),
      actualName: Object.values(overallData?.overallData)
        .map((arr) => arr[i]?.section)
        .filter((section) => section !== undefined),
    }));

    console.log("    series", series);

    // Define colors for 9 different bars (3 for Reading, 3 for Writing, 3 for Listening)
    const colors = [
      "#D78D2F", // Reading test 1
      "#507BB9", // writing test 1
      "#4DAE92", // Listening test 1
      "#ED8363", // Speaking  test 1
      "#56A6DB", // Mock  test 1

      "#FFE1BC", // Reading test 2
      "#A1C7FE", // writing test 2
      "#92E8D0", // Listening test 2
      "#FEAC94", // Speaking  test 2
      "#99D4FB", // Mock  test 2

      "#FFF6EA", // Reading test 3
      "#C7DDFE", // writing test 3
      "#C6F6E9", // Listening test 3
      "#FFD8CD", // Speaking  test 3
      "#C5E8FF", // Mock  test 3
    ];

    // Assign the correct categories and colors to each test
    const newSeries = series.map((seriesItem, index) => ({
      name: seriesItem.name,
      data: seriesItem.data.map((score, i) => ({
        x: categories[i], // Use the correct category for each test (e.g., Reading, Writing)
        y: score,
        actualName: seriesItem.actualName[i],
        fillColor: colors[i + index * 5], // Apply different color for each test's bar
      })),
    }));

    console.log("    newSeries", newSeries);

    // Update chart data with API response
    setChartData({
      series: [...newSeries],
      options: {
        ...chartData.options,
        xaxis: {
          ...chartData.options.xaxis,
          categories: [...categories], // Set categories from API data
        },
        yaxis: {
          ...chartData.options.yaxis,
          max: overallData?.marksRange?.max, // Max value for the Y-axis
        },
        // tooltip: {
        //   ...chartData.options.tooltip,
        //   y: {
        //     formatter: function (val) {
        //       return `Band ${val}/${overallData?.marksRange?.max}`; // Formatting tooltip values
        //     },
        //   },
        // },
        tooltip: {
          custom: function ({ seriesIndex, dataPointIndex, w }) {
            const seriesName = w.config.series[seriesIndex]?.name;
            const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];

            console.log("seriesIndex", seriesIndex, dataPointIndex, w);
            return `
      <div style="padding:8px">
        <strong> ${dataPoint.actualName}</strong><br/>
        Band : ${dataPoint.y} / ${overallData?.marksRange?.max}
       
      </div>
    `;
          },
        },
      },
    });
  }, [overallData]); // Run the effect whenever `overallData` changes

  const redirectionLink1 = "/test-prep/performance/my-exam";
  const handleRedirect = () => {
    localStorage.setItem("defaultExamData", JSON.stringify(testTypeData));
    window.open(redirectionLink1, "_blank");
  };

  const hasOverallData = (data) => {
    if (!data) return false;

    return Object.values(data).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    );
  };

  const showChart = hasOverallData(overallData?.overallData);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography
          fontFamily="Inter"
          color="#161719"
          sx={{ fontWeight: 600, fontSize: "18px" }}
        >
          Last 3 Comparison Progress
        </Typography>
      </Grid>

      {loading ? (
        <Grid item xs={12}>
          <Loader text="Loading Test Details..." height="30vh" />
        </Grid>
      ) : !loading && showChart ? (
        <Grid item xs={12}>
          <Chart
            options={chartData.options}
            series={chartData.series}
            type="bar"
            height={245}
          />
        </Grid>
      ) : (
        <NoDataAvailabWithNoCardleScreen
          topImage={
            <img src="/newdashboard/performance/20769812_Sandy_Bus-25_Single-04 1 (1).png" />
          }
          //  text="No Test Type Purchased Yet"
          subText="You haven’t completed the test yet.Finish the test to view your results."
          buttonName="Complete Test"
          // buttonIcon={
          //   <Icon icon="material-symbols:crown-rounded" />
          // }
          imageSize={120}
          showTopImage={true}
          redirectionLink={handleRedirect}
        />
      )}
    </Grid>
  );
}
